import { ColumnsType } from '@seiue/ui'
import { Badge, Tooltip } from 'antd'
import {
  ChildField,
  ChildFieldResColumn,
  FieldDomainEnum,
  FieldGroupRes,
  FieldRes,
  FieldTypeEnum,
  Guardian,
  InformalReflection,
  ReflectionGenderEnum,
  ReflectionIdcardTypeEnum,
  ReflectionPupilTypeEnum,
  ReflectionRoleEnum,
  ReflectionStatusEnum,
  TermReq,
} from 'packages/sdks-next/growp'
import { AnyType, AnyValueObj } from 'packages/types'
import {
  InfoTypeEnumToSearchFormType,
  genderToLabel,
  guardianTypeToLabel,
  idcardTypeToLabel,
  roleToLabel,
  statusToColor,
  statusToLabel,
} from 'packages/utils'
import styled from 'styled-components'
import { ColumnsRender } from './ColumnsRender'
import { createEnumHelper, isArray } from '@seiue/util'
import { CusFieldRespond } from './types'
import {
  FormConfig,
  FormItemConfig,
  ISearchFormItemType,
  paramsFormat,
} from 'packages/components/DataTable'
import {
  ChildFieldResColumnWithMetaData,
  FieldResWithMetaData,
} from '../FieldManagement'
import {
  accurateSearchKeys,
  guardiansFixedKeys,
  notEditableKeys,
  selectNewKey,
} from './info'
import humps from 'humps'
import { IGroupItem } from '../MultiExport'

/**
 * 导出列表Tag配置
 * @param param.groups 接口返回原始分组数组
 * @returns Tag 格式化后List数据
 */
export const getExportConfig = (
  groups: FieldGroupRes[] | null,
  domain: FieldDomainEnum
): IGroupItem[] => {
  const finalGroups = groups ? groups : []
  const config = finalGroups.map((group: FieldGroupRes) => {
    const fields = group.fields || []
    const groupList = fields
      .filter((field: FieldResWithMetaData) => {
        const { originLabel } = field.metadata || {}
        if (
          /^avatar|account$/.test(originLabel || '') ||
          ([
            FieldDomainEnum.FormalStudent,
            FieldDomainEnum.FormalTeacher,
          ].includes(domain) &&
            originLabel === 'activity_names')
        ) {
          return false
        }
        return true
      })
      .map((field) => {
        return {
          label: field.label,
          key: field.name,
        }
      })
    return {
      groupLabel: group.name,
      groupList,
    }
  })
  return config
}

/**
 * 筛选请求参数格式化
 * @param params 筛选表单提交的数据
 * @returns API接收的最终参数
 */
export const formatInformStuSearchParams = (params: AnyValueObj) => {
  let finalParams: AnyValueObj = { ...params }
  Object.keys(params).map((key) => {
    let deleteOriginKey = true
    if (accurateSearchKeys.includes(key)) {
      return
    } else if (/^guardians.(0|1)./.test(key)) {
      let replaceStr = guardiansFixedKeys.includes(key)
        ? 'guardians.0.'
        : 'guardians.0.field.'
      if (/^guardians.1./.test(key)) {
        replaceStr = guardiansFixedKeys.includes(key)
          ? 'guardians.1.'
          : 'guardians.1.field.'
      }
      let newKey = key.replace(/^guardians.(0|1)./, replaceStr)
      let value = params[key]
      if (selectNewKey[key]) {
        if (/.phone$/.test(key)) {
          value = value.replace(/，/g, ',')
        }
        newKey = selectNewKey[key](params[key])
        deleteOriginKey = true
      }
      const newParams = paramsFormat(newKey, value)
      finalParams = { ...finalParams, ...newParams }
      guardiansFixedKeys.includes(key) ? (deleteOriginKey = false) : null
    } else if (/^name|phone$/.test(key)) {
      const newKey = /，|,/.test(params[key]) ? `${key}In` : `${key}Like`
      finalParams[newKey] = params[key].replace(/，|,/g, ',')
    } else {
      const newParams = paramsFormat(
        key,
        params[key],
        !notEditableKeys.includes(key)
      )
      finalParams = { ...finalParams, ...newParams }
    }
    if (deleteOriginKey) {
      finalParams[key] = undefined
    }
  })
  return finalParams
}

/**
 * 列表数据格式化
 * @param data Inform-student API返回的数据
 * @returns 格式化后的表格数据
 */
export const formatInformStuDataSource = (data: InformalReflection[]) => {
  const dataSource = data.map((d: InformalReflection) => {
    const obj: AnyValueObj = {}
    if (d.guardians) {
      d.guardians.map((g: Guardian) => {
        const guardianLevel = g['guardianLevel']
        for (const k in g) {
          if (k === 'fields' && g['fields']) {
            const fields: AnyValueObj = g['fields']
            Object.keys(fields).map((key) => {
              obj[`guardians.${guardianLevel}.${key}`] = fields[key]
            })
          } else {
            obj[`guardians.${guardianLevel}.${k}`] = g[k as keyof Guardian]
          }
        }
      })
    }
    if (d.fields) {
      const fields = d.fields
      Object.keys(fields).map((key) => {
        obj[key] = fields[key as keyof object]
      })
    }
    const finalObj = {
      ...d,
      ...obj,
    }
    return finalObj
  })
  return dataSource
}

export const optShowLabel: { [key: string]: (name: AnyType) => void } = {
  role: (name: ReflectionRoleEnum) => roleToLabel.get(name),
  gender: (name: ReflectionGenderEnum) => genderToLabel.get(name),
  status: (name: ReflectionStatusEnum) => statusToLabel.get(name),
  idcardType: (name: ReflectionIdcardTypeEnum) => idcardTypeToLabel.get(name),
  'guardians.0.pupilType': (name: ReflectionPupilTypeEnum) =>
    guardianTypeToLabel.get(name),
  'guardians.1.pupilType': (name: ReflectionPupilTypeEnum) =>
    guardianTypeToLabel.get(name),
}

/**
 * 获取选项标签
 *
 * @param field - 字段
 * @param name - 字段名
 * @returns 标签
 */
const getOptionLabel = (
  field: ChildField | ChildFieldResColumn | FieldRes,
  name: string
) => {
  if (
    /^(guardians.0.pupil_type|guardians.1.pupil_type)$/.test(field.name || '')
  ) {
    return guardianTypeToLabel.get(name as ReflectionPupilTypeEnum)
  }
  if (/^idcard_type$/.test(field.name || '')) {
    return idcardTypeToLabel.get(name as ReflectionIdcardTypeEnum)
  }
  if (field.name && optShowLabel[field.name]) {
    return optShowLabel[field.name](name)
  }
  return name
}

/**
 * 获取表单配置
 *
 * @param field 字段
 * @returns 配置
 */
export const getFormItemConfigs = (
  field: ChildFieldResColumnWithMetaData | FieldResWithMetaData
): FormItemConfig => {
  const { originLabel } = field.metadata || {}
  return {
    type: InfoTypeEnumToSearchFormType.get(field.type),
    label: field.label,
    name: originLabel || field.name,
    otherConfig: {
      options: field.options
        ?.filter((opt) => !opt.deletedAt)
        .map((opt: TermReq) => {
          return {
            label: getOptionLabel(field, opt.name) || opt.name,
            value: [
              'gender',
              'status',
              'idcardType',
              'guardians.0.pupilType',
              'guardians.1.pupilType',
            ].includes(humps.camelize(originLabel || ''))
              ? opt.name
              : `${opt.id}` || '',
          }
        }),
    },
  }
}

/**
 * 获取用户 datatable 筛选配置
 *
 * @param finalGroupsData 字段组
 * @returns 配置
 */
export const getSearchFormConfig = (
  finalGroupsData: FieldGroupRes[],
  domain: FieldDomainEnum
): FormConfig => {
  const config: FormConfig = {}
  finalGroupsData.map((group) => {
    let itemList: FormItemConfig[] = []
    const groupFields = group.fields ? group.fields : []
    groupFields
      .filter((item: FieldRes) => {
        if (
          /^attachment$/.test(item.type) ||
          ([
            FieldDomainEnum.FormalTeacher,
            FieldDomainEnum.FormalStudent,
          ].includes(domain)
            ? /^(avatar)$/
            : /^(avatar|account)$/
          ).test(item.name) ||
          !item.isShow ||
          (item.type === FieldTypeEnum.Product && item.label !== '证件') ||
          ([
            FieldDomainEnum.FormalStudent,
            FieldDomainEnum.FormalTeacher,
          ].includes(domain) &&
            item.name === 'activity_names')
        ) {
          return false
        }
        return true
      })
      .map((field: FieldRes) => {
        if (field.type === 'product' && field.children) {
          if (field.label === '证件') {
            itemList = [
              ...itemList,
              ...[
                {
                  type: ISearchFormItemType.Select,
                  label: '证件类型',
                  name: 'idcard_type',
                  otherConfig: {
                    options: idcardTypeToLabel.listOptions('value', 'label'),
                  },
                },
                {
                  type: ISearchFormItemType.Input,
                  label: '证件号',
                  name: 'idcard',
                },
              ],
            ]
          } else {
            field.children.map((child) => {
              itemList.push(getFormItemConfigs(child))
            })
          }
        } else {
          itemList.push(getFormItemConfigs(field))
        }
      })
    config[group.name] = itemList
  })
  return config
}

export const domainToFieldSetting = createEnumHelper(() => ({
  [FieldDomainEnum.FormalStudent]: 'InternalStudentsFieldSetting',
  [FieldDomainEnum.FormalTeacher]: '',
  [FieldDomainEnum.InformalStudent]: 'StuResourceFieldsManagement',
  [FieldDomainEnum.InformalTeacher]: 'FieldSetting',
}))

export const domainToApiName = createEnumHelper(() => ({
  [FieldDomainEnum.InformalTeacher]:
    'InformalReflectionApi.queryInformalTeachers',
  [FieldDomainEnum.InformalStudent]:
    'InformalReflectionApi.queryInformalStudents',
  [FieldDomainEnum.FormalStudent]: 'ReflectionApi.queryStudents',
  [FieldDomainEnum.FormalTeacher]: 'ReflectionApi.queryTeachers',
}))

/**
 * 特殊字段映射
 */
export const colRenderSelector: {
  [key: string]: (text: AnyType) => JSX.Element | string
} = {
  role: (text?: ReflectionRoleEnum) => (text ? roleToLabel.get(text) : '-'),
  gender: (text?: ReflectionGenderEnum) =>
    text ? genderToLabel.get(text) : '-',
  status: (text?: ReflectionStatusEnum) => (
    <Badge
      color={text ? statusToColor.get(text) : 'yellow'}
      text={text ? statusToLabel.get(text) : '-'}
    />
  ),
  idcardType: (text: ReflectionIdcardTypeEnum | null) =>
    text ? idcardTypeToLabel.get(text) : '-',
  'guardians.0.pupilType': (text?: ReflectionPupilTypeEnum) =>
    text ? guardianTypeToLabel.get(text) : '-',
  'guardians.1.pupilType': (text?: ReflectionPupilTypeEnum) =>
    text ? guardianTypeToLabel.get(text) : '-',
}

/**
 * URL字符串在页面中显示
 * @param param.str - str 任意字符串
 * @returns 显示结果
 */
export const urlRender = (str: string | number) => {
  const urlPattern =
    /^(((ht|f)tps?):\/\/)?([^!@#$%^&*?.\s-]([^!@#$%^&*?.\s]{0,63}[^!@#$%^&*?.\s])?\.)+[a-z]{2,6}\/?/
  if (typeof str === 'string' && urlPattern.test(str)) {
    return (
      <a
        target="_blank"
        rel="noreferrer"
        href={str}
      >
        {str}
      </a>
    )
  }
  return str
}

/**
 * Multi，Single类型数据ValueToLabel
 *
 * @param data 原始数据
 * @param type 数据属于哪种类型
 * @param options 可选选项
 * @returns 处理后的数据
 */
export const optionValueToLabel = (
  data: string[] | number[] | string | number,
  type: FieldTypeEnum,
  options?: TermReq[]
) => {
  const valueToLabel: { [key: number]: string } = {}
  if (options) {
    options.map((option) => {
      if (option.id) {
        valueToLabel[option.id] = option.name
      }
    })
  }
  if (type === FieldTypeEnum.Multi && isArray(data)) {
    return data.map((id) => valueToLabel[Number(id)] || id)
  }
  if (type === FieldTypeEnum.Single) {
    return valueToLabel[Number(data)] || data
  }
  return data
}

/**
 * 获取子表表头
 * @param data 子表表头相关信息
 * @returns 子表表头
 */
export const getChildrenColumn = (
  data: CusFieldRespond[],
  isIdCard = false
) => {
  let childrenColumn: ColumnsType<AnyType> = []
  if (isIdCard) {
    childrenColumn = [
      {
        title: '证件类型',
        key: 'idcard_type',
        dataIndex: 'idcard_type',
      },
      {
        title: '证件号',
        key: 'idcard',
        dataIndex: 'idcard',
      },
    ]
  } else {
    childrenColumn = data.map((i) => {
      return {
        title: (
          <Tooltip
            placement="topLeft"
            title={i.label}
            overlayStyle={{
              minWidth: '50px',
            }}
          >
            {i.label}
          </Tooltip>
        ),
        key: i.name,
        dataIndex: i.name,
        render: (text) => {
          return text ? (
            <DataRenderWrapper>
              <ColumnsRender
                field={i}
                text={text}
              />
            </DataRenderWrapper>
          ) : (
            text
          )
        },
      }
    })
  }
  childrenColumn.unshift({
    title: '序号',
    render: (t, r, i) => `${i + 1}`,
  })
  return childrenColumn
}

/**
 * 获取子表信息
 * @param record 父表当前行数据
 * @param parentCol 父表表头相关信息
 * @returns 子表数据
 */
export const getChildrenDataSource = (
  record: InformalReflection,
  parentCol: CusFieldRespond
) => {
  if (parentCol.label === '证件') {
    return [
      {
        id: '1',
        // eslint-disable-next-line
        idcard_type: record.idcardType
          ? idcardTypeToLabel.get(record.idcardType)
          : record.idcardType,
        idcard: record.idcard,
      },
    ]
  }
  const fields = record.fields || {}
  try {
    const value = fields[parentCol.name as keyof object]
    if (Array.isArray(value)) {
      return value
    }
    return value
  } catch (error) {
    // eslint-disable-next-line
    console.error(error)
  }
}

const DataRenderWrapper = styled.div`
  max-width: 120px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`
